// src/components/result/ResultPreview.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDateLong, formatTime } from "../../utils/time";
import { ResultCard } from "./ResultCard";
import { ResultStatGrid } from "./ResultStatGrid";
import { ChartBlock } from "./ChartBlock";
import { LockOverlay } from "../ui/LockOverlay";
import MetricLineChart from "../charts/MetricLineChart";

export function ResultPreview({
  recording = "exertionRecovery",
  activityDate,
  stats, // optional override: [{label, value, hint}]
  chart, // optional override: a React node
  chartTitle, // override title for chart block
  showLockedUI,
  lockProps, // passthrough to <LockOverlay />
  titleKey = "activity.exertion-recovery.title",
  titleDefault = "Introduction Test",
  infoKey,
  locks = {
    lungAge: true,
    lungFitness: true,
    respiratoryRate: false,
    breathingRecovery: true,
    recoveryTime: true,
    recoveryCurve: true,
    totalVolume: true,
  },
  cta = null, // optional: { label, to, ariaLabel }
  onUnlock,
}) {
  const { t } = useTranslation();

  const subtitle = useMemo(() => {
    const d =
      activityDate instanceof Date ? activityDate : new Date(activityDate);
    return `${formatDateLong(d)} · ${formatTime(d)}`;
  }, [activityDate]);

  const defaultStats = useMemo(
    () => [
      {
        label: t("activity.exertion-recovery.stat2", { defaultValue: "Time" }),
        value: "7:00",
        hint: t("activity.exertion-recovery.stat2hint", {
          defaultValue: "Test",
        }),
      },
      {
        label: t("activity.exertion-recovery.stat4", {
          defaultValue: "Resp. Rate",
        }),
        value: "22",
        hint: t("activity.exertion-recovery.stat4hint", {
          defaultValue: "Test",
        }),
      },
      {
        label: t("activity.exertion-recovery.stat1", {
          defaultValue: "Recovery",
        }),
        value: "",
        hint: t("activity.exertion-recovery.stat1hint", {
          defaultValue: "Locked",
        }),
      },
      {
        label: t("activity.exertion-recovery.stat3", {
          defaultValue: "Rec. Time",
        }),
        value: "",
        hint: t("activity.exertion-recovery.stat3hint", {
          defaultValue: "Locked",
        }),
      },
    ],
    [t]
  );

  return (
    <ResultCard
      title={t(titleKey, { defaultValue: titleDefault })}
      subtitle={subtitle}
    >
      <ResultStatGrid items={stats ?? defaultStats} locks={locks} />

      <ChartBlock
        title={
          chartTitle ??
          t("activity.exertion-recovery.chartTitle", {
            defaultValue: "Recovery Curve",
          })
        }
        infoKey={infoKey}
      >
        {chart ?? (
          <MetricLineChart
            height={160}
            datasets={[]} // default to an empty chart
            preset="none" // ensure no decay preset
            showLegend={false}
            showTooltip={false}
          />
        )}
        {/* Lock the chart only if its underlying metric is locked.
            For Exertion we use 'recoveryCurve'; for Spirometry swap to 'breathingTrace' if you add it. */}
        <LockOverlay
          show={
            recording === "spirometry"
              ? locks.breathingTrace
              : locks.recoveryCurve
          }
          {...lockProps}
          to={cta?.to}
          onClick={onUnlock}
          ctaKey="activity.lockedCta"
          ctaDefault={cta?.label ?? "Unlock Results"}
          ariaKey="activity.lockedCtaAria"
          ariaDefault={cta?.ariaLabel ?? "Go to checkout to unlock results"}
        />
      </ChartBlock>
    </ResultCard>
  );
}
